import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Switch,
  Snackbar,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BadgeIcon from '@mui/icons-material/Badge';
import PinDropIcon from '@mui/icons-material/PinDrop';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../../constants/constants';
import BackButton from '../../../components/BackButton';
import { LEVEL_NAMES } from '../../../constants/userLevels';

const UserLevelListing = () => {
  const [searchParams] = useSearchParams();
  const levelParam = searchParams.get('level') || '3';
  const targetLevel = parseInt(levelParam, 10);

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [requesterLevel, setRequesterLevel] = useState(1);
  const [requesterId, setRequesterId] = useState('');
  const [error, setError] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const navigate = useNavigate();

  const getLevelInfo = (lvl) => {
    switch (lvl) {
      case 2:
        return { title: `${LEVEL_NAMES[2]}s`, roleBadge: `Level 2 ${LEVEL_NAMES[2]}`, color: '#8b5cf6' };
      case 3:
        return { title: `${LEVEL_NAMES[3]}s`, roleBadge: `Level 3 ${LEVEL_NAMES[3]}`, color: '#ec4899' };
      case 4:
        return { title: `${LEVEL_NAMES[4]}s`, roleBadge: `Level 4 ${LEVEL_NAMES[4]}`, color: '#6366f1' };
      case 5:
        return { title: `${LEVEL_NAMES[5]}s`, roleBadge: `Level 5 ${LEVEL_NAMES[5]}`, color: '#3b82f6' };
      default:
        return { title: 'System Users', roleBadge: `Level ${lvl} User`, color: '#0f172a' };
    }
  };

  const levelInfo = getLevelInfo(targetLevel);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setRequesterLevel(decoded.level || 1);
        setRequesterId(decoded.userId || '');
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/users/by-level`, {
        params: {
          level: targetLevel,
          requesterLevel,
          userId: requesterId,
        },
      });

      if (Array.isArray(response.data)) {
        setUsers(response.data);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users by level:', err);
      setError('Failed to fetch user profiles. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [targetLevel, requesterLevel, requesterId]);

  useEffect(() => {
    if (requesterId || requesterLevel === 1) {
      fetchUsers();
    }
  }, [fetchUsers, requesterId, requesterLevel]);

  // Filter users based on search term and status filter
  useEffect(() => {
    let result = users;

    if (statusFilter !== 'ALL') {
      result = result.filter((u) => u.status && u.status.toLowerCase() === statusFilter.toLowerCase());
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((u) => {
        const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        const email = (u.email || '').toLowerCase();
        const phone = (u.phoneNumber || '').toLowerCase();
        const pin = (u.pinCode || '').toLowerCase();
        return fullName.includes(term) || email.includes(term) || phone.includes(term) || pin.includes(term);
      });
    }

    setFilteredUsers(result);
  }, [users, searchTerm, statusFilter]);

  const handleToggleStatus = async (user) => {
    try {
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      await axios.patch(`${API_BASE_URL}/update-status/${user._id}`, {
        status: newStatus,
        requesterId,
      });

      setUsers((prev) =>
        prev.map((u) => (u._id === user._id ? { ...u, status: newStatus } : u))
      );
      setSnackbarMessage(`User status changed to ${newStatus}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error updating user status:', err);
      const msg = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Failed to update user status';
      setSnackbarMessage(msg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'User Profile',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: `${levelInfo.color}15`,
              color: levelInfo.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '0.875rem',
            }}
          >
            {(params.row.firstName || 'U')[0].toUpperCase()}
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {params.row.firstName} {params.row.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {params.row._id ? params.row._id.slice(-6) : 'N/A'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'contact',
      headerName: 'Contact Information',
      flex: 1.5,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmailIcon sx={{ fontSize: 13, color: '#64748b' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155' }}>
              {params.row.email || 'N/A'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 13, color: '#64748b' }} />
            <Typography variant="caption" color="text.secondary">
              {params.row.phoneNumber || 'N/A'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'pinCode',
      headerName: 'Area Pincode',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          icon={<PinDropIcon style={{ fontSize: 14, color: '#0284c7' }} />}
          label={params.value || 'N/A'}
          variant="outlined"
          sx={{ fontWeight: 700, borderColor: '#e0f2fe', color: '#0369a1', backgroundColor: '#f0f9ff' }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Account Status',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        const isActive = (params.value && params.value.toLowerCase()) === 'active';
        return (
          <Chip
            icon={isActive ? <CheckCircleIcon style={{ fontSize: 14 }} /> : <CancelIcon style={{ fontSize: 14 }} />}
            label={isActive ? 'ACTIVE' : 'INACTIVE'}
            color={isActive ? 'success' : 'default'}
            size="small"
            sx={{ fontWeight: 800, borderRadius: 2 }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Quick Action',
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={`Toggle ${params.row.firstName}'s Status`}>
            <Switch
              size="small"
              checked={(params.row.status && params.row.status.toLowerCase()) === 'active'}
              onChange={() => handleToggleStatus(params.row)}
              color="primary"
            />
          </Tooltip>
        </Box>
      ),
    },
  ];

  const activeCount = users.filter((u) => u.status && u.status.toLowerCase() === 'active').length;
  const inactiveCount = users.length - activeCount;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <BackButton />
      <Box sx={{ mt: 2, mb: 3 }}>
        {/* Header Ribbon */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3.5,
            border: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {levelInfo.title}
              </Typography>
              <Chip label={levelInfo.roleBadge} sx={{ bgcolor: `${levelInfo.color}15`, color: levelInfo.color, fontWeight: 700 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Comprehensive user directory with real-time filters, search options, and account status controls
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Refresh User List">
              <IconButton onClick={fetchUsers} color="primary" sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5 }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Analytics Cards */}
        <Grid container spacing={2.5} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Total Accounts
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                    {users.length}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 36, color: levelInfo.color }} />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Active Accounts
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#10b981', mt: 0.5 }}>
                    {activeCount}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 36, color: '#10b981' }} />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Inactive Accounts
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#ef4444', mt: 0.5 }}>
                    {inactiveCount}
                  </Typography>
                </Box>
                <CancelIcon sx={{ fontSize: 36, color: '#ef4444' }} />
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Filter Bar */}
        <Paper elevation={0} sx={{ p: 2.5, mt: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by Name, Email, Mobile Number, or Area Pincode..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                {['ALL', 'ACTIVE', 'INACTIVE'].map((st) => (
                  <Chip
                    key={st}
                    label={st}
                    clickable
                    onClick={() => setStatusFilter(st)}
                    color={statusFilter === st ? 'primary' : 'default'}
                    variant={statusFilter === st ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* DataGrid View */}
        <Paper elevation={0} sx={{ mt: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            getRowId={(row) => row._id}
            loading={loading}
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                fontWeight: 700,
                color: '#475569',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f8fafc',
              },
            }}
          />
        </Paper>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserLevelListing;
